import {
  type BrowserAuthOptions,
  type BrowserCacheLocation,
  type BrowserSystemOptions,
  type CacheOptions,
  type Configuration,
  LogLevel,
  type RedirectRequest,
} from '@azure/msal-browser';

const sharedCacheConfig: CacheOptions = {
  cacheLocation: 'localStorage' as BrowserCacheLocation,
  storeAuthStateInCookie: false,
};

const sharedAuthConfig: BrowserAuthOptions = {
  clientId: globalThis.appConfig.auth.clientId,
  authority: globalThis.appConfig.auth.authority,
  knownAuthorities: [globalThis.appConfig.auth.knownAuthority],
  redirectUri:
    globalThis.appConfig.auth.staticLoginRedirectUri ?? window.location.origin,
  postLogoutRedirectUri: `${window.location.origin}/login?authType=sso`,
};

const msalLoggerCallback = (
  level: LogLevel,
  message: string,
  containsPii: boolean
): void => {
  if (containsPii) {
    console.log('CS Web - MSAL operation contains PII, skipping...');
    return;
  }

  const logMethods: Record<LogLevel, typeof console.log> = {
    [LogLevel.Error]: console.error,
    [LogLevel.Info]: console.log,
    [LogLevel.Trace]: console.trace,
    [LogLevel.Verbose]: console.debug,
    [LogLevel.Warning]: console.warn,
  };

  const consoleOperation = logMethods[level] ?? console.log;
  consoleOperation('CS Web - MSAL: ', message);
};

const sharedSystemConfig: BrowserSystemOptions = {
  // Increase iframe timeout for corporate networks with high latency (e.g., GM network)
  // Default is 10000ms which is insufficient when network round-trips are 400-500ms
  // See: SDPCDOS-1732, docs/reports/MSAL_Monitor_Window_Timeout_Issue_Report.md
  iframeHashTimeout: 30000,
  windowHashTimeout: 60000,
  ...(process.env.NODE_ENV !== 'production' && {
    loggerOptions: {
      loggerCallback: msalLoggerCallback,
      piiLoggingEnabled: false,
    },
  }),
  // TODO - SDPCPLT-6982 - temporary increase to 15 minutes (15 * 60 = 900) to test token refresh related to an issue of expired token on backend
  tokenRenewalOffsetSeconds: 900, // 15 minutes
};

export const msalConfig: Configuration = {
  auth: sharedAuthConfig,
  cache: sharedCacheConfig,
  system: sharedSystemConfig,
};

export const msalCdiConfig: Configuration = {
  auth: {
    ...sharedAuthConfig,
    authority: globalThis.appConfig.auth.cdiAuthority,
    postLogoutRedirectUri: `${window.location.origin}/login`,
  },
  cache: sharedCacheConfig,
  system: sharedSystemConfig,
};

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest: RedirectRequest = {
  scopes: [globalThis.appConfig.auth.clientId],
  prompt: 'login',
  //max_age corresponds to how long the session is alive for before re-authentication is required
  //extra query parameter does not seem to actually change session or refresh token lifetime?
  extraQueryParameters: {
    max_age: globalThis.appConfig.auth.maxAge,
  },
};

export const createCdiAccountRequest: RedirectRequest = {
  ...loginRequest,
  authority: globalThis.appConfig.auth.cdiAuthority,
  redirectUri: window.location.origin,
  scopes: [globalThis.appConfig.auth.clientId],
  extraQueryParameters: {
    ...loginRequest.extraQueryParameters,
    scope: 'openid',
    channel: 'globalnav',
    brand: 'gmfleet',
  },
};

export const DEFAULT_TIMEOUT = 1800; // seconds
export const TIMEOUT_MODAL_THRESHOLD = 450; // seconds
export const LOGIN_ATTEMPT_TIMEOUT = 10000;
export const MAX_LOGIN_ATTEMPTS = 3;
export const URL_CLEANUP_DELAY_MS = 5; // milliseconds
